import React, { useEffect } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { ProfileLayout } from './ProfileLayout';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useEntityPhrasing } from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { SlDropdownMenu, SlDropdownMenuProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { Grid, Skeleton, Divider } from '@mui/material';
import SlPagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { ProfileCard } from './ProfileSummaryCard';
import { useAppDispatch, useAppSelector } from 'src/utils/Hooks';
import { fetchUserTeam } from 'src/store/modules/user/operations';
import { useGetProfiles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { useProfileSummaryStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { StatusType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { RightArrow } from 'src/components/SvgIcons/SvgIcons';
import colors from 'src/utils/colors';

export const DropdownMenu = (props: SlDropdownMenuProps) => {
  return (
    <SlDropdownMenu
      menuItemTextStyles={{ fontWeight: 500 }}
      iconStyle={{ marginLeft: '10px' }}
      menuStyles={{
        maxHeight: '288px',
        '& .MuiMenuItem-root': {
          paddingTop: '8px',
          paddingBottom: '8px'
        }
      }}
      {...props}
    />
  );
};

const ProfileCardSkeleton = () => {
  return (
    <Flex
      flexDirection="column"
      sx={{
        border: `1px solid ${colors.graysSecondary}`,
        borderRadius: '8px',
        boxSizing: 'border-box',
        padding: '16px',
        width: '336px',
        height: '153px',
        backgroundColor: 'white'
      }}
    >
      <Skeleton variant="text" width="80%" height={24} sx={{ marginBottom: '4px', backgroundColor: '#F6F7F8' }} />
      <Skeleton variant="text" width="60%" height={24} sx={{ backgroundColor: '#F6F7F8' }} />
      <Divider sx={{ marginTop: 'auto', marginBottom: '16px' }} />
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center">
          <Skeleton
            variant="circular"
            width={30}
            height={30}
            sx={{ marginRight: '8px', flexShrink: 0, backgroundColor: '#F6F7F8' }}
          />
          <Flex flexDirection="column">
            <Skeleton variant="text" width={100} height={16} sx={{ backgroundColor: '#F6F7F8' }} />
            <Skeleton variant="text" width={140} height={14} sx={{ backgroundColor: '#F6F7F8' }} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

type ProfileSummaryProps = {
  selectedId?: StatusType;
};

export const ProfileSummary = ({ selectedId = 'ACTIVE' }: ProfileSummaryProps) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.user.session?.userId);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [ownerFilter, setOwnerFilter] = React.useState<'ALL' | 'OWNED'>('ALL');
  const [page, setPage] = React.useState(1);
  const ITEMS_PER_PAGE = 30;
  const viewMode = useProfileSummaryStore((s) => s.viewMode);
  const setStoreViewMode = useProfileSummaryStore((s) => s.setViewMode);
  const setStoreEditingId = useProfileSummaryStore((s) => s.setEditingProfileId);
  const { beaconNavigator } = useBeaconNavigator();

  // Profile list data
  const { data, isLoadingProfiles, refetchProfiles } = useGetProfiles(selectedId);

  useEffect(() => {
    dispatch(fetchUserTeam('/api/user/GetAllUsers'));
  }, [dispatch]);

  const handleProfileArchived = () => {
    refetchProfiles();
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleAddProfile = () => {
    setStoreViewMode('add');
    setStoreEditingId('');
    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROFILES,
      subtab: BEACON_SUBTABS.PROFILES_SETTINGS,
      mode: 'add'
    });
    beaconNavigator.go();
  };

  const handleEditProfile = (profileId: string) => {
    setStoreViewMode('edit');
    setStoreEditingId(profileId);
    beaconNavigator.updateSearchParams({
      tab: BEACON_TABS.PROFILES,
      subtab: BEACON_SUBTABS.PROFILES_SETTINGS,
      mode: 'edit',
      profileId
    });
    beaconNavigator.go();
  };

  const ownedFilteredProfiles =
    ownerFilter === 'OWNED' && data?.data
      ? data.data.filter((profile: any) => profile.createdBy === userId)
      : data?.data;

  const filteredProfiles = ownedFilteredProfiles?.filter((profile) =>
    profile.profileName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort: Stackline-generated profiles first, then by updatedAt (most recent first)
  const sortedProfiles = filteredProfiles?.slice().sort((a, b) => {
    const isStacklineA = a.createdBy === 'content-generation-workflow';
    const isStacklineB = b.createdBy === 'content-generation-workflow';

    // Stackline profiles always come first
    if (isStacklineA && !isStacklineB) return -1;
    if (!isStacklineA && isStacklineB) return 1;

    // Otherwise sort by updatedAt (most recent first)
    const dateA = new Date(a.updatedAt).getTime();
    const dateB = new Date(b.updatedAt).getTime();
    return dateB - dateA; // Descending order (newest first)
  });

  const totalItems = sortedProfiles?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const paginatedProfiles = sortedProfiles?.slice(startIndex, startIndex + ITEMS_PER_PAGE) || [];

  // Reset page when filters change or result set size shrinks below current page
  React.useEffect(() => {
    setPage(1);
  }, [searchQuery, ownerFilter, selectedId]);

  const entityPhrase = useEntityPhrasing();

  // Loading state for list view
  if (isLoadingProfiles && viewMode === 'list') {
    return (
      <BasePageContainer>
        <ProfileLayout>
          <Flex flexDirection="column">
            <Flex justifyContent="space-between" alignItems="center" marginBottom="24px">
              <Text
                variant="body1"
                sx={{
                  marginTop: '8px',
                  lineHeight: '24px'
                }}
              >
                Listed below are all the content profiles for {entityPhrase}. You can create a new profile or edit an
                existing one.
              </Text>
            </Flex>
            <Flex justifyContent="space-between">
              <SearchTextField placeholder="Search profiles" value="" onChange={() => {}} disabled />
              <Flex gap={16}>
                <DropdownMenu
                  selectedId="ALL"
                  options={[
                    { id: 'ALL', label: 'View All' },
                    { id: 'OWNED', label: 'View Owned' }
                  ]}
                  onChange={() => {}}
                  disabled
                />
              </Flex>
            </Flex>
            <Grid container spacing={2} mt={2}>
              {Array.from({ length: 9 }).map((_, index) => (
                <Grid item xs={4} key={index}>
                  <ProfileCardSkeleton />
                </Grid>
              ))}
            </Grid>
          </Flex>
        </ProfileLayout>
      </BasePageContainer>
    );
  }

  // Render list view
  return (
    <BasePageContainer>
      <ProfileLayout>
        <Flex flexDirection="column">
          <Flex justifyContent="space-between" alignItems="center" marginBottom="32px">
            <Text
              variant="body1"
              sx={{
                marginTop: '8px',
                lineHeight: '24px'
              }}
            >
              Listed below are all the content profiles for {entityPhrase}. You can create a new profile or edit an
              existing one.
            </Text>
          </Flex>
          <Flex justifyContent="space-between">
            <SearchTextField placeholder="Search profiles" value={searchQuery} onChange={handleSearchChange} />
            <Flex gap={16}>
              <DropdownMenu
                selectedId={ownerFilter}
                options={[
                  { id: 'ALL', label: 'View All' },
                  { id: 'OWNED', label: 'View Owned' }
                ]}
                onChange={(val) => {
                  setOwnerFilter(val.id as 'ALL' | 'OWNED');
                }}
              />
            </Flex>
          </Flex>
          <Grid container spacing={2} mt={2}>
            {isLoadingProfiles ? (
              Array.from({ length: 9 }).map((_, index) => (
                <Grid item xs={4} key={index}>
                  <ProfileCardSkeleton />
                </Grid>
              ))
            ) : sortedProfiles && sortedProfiles.length > 0 ? (
              paginatedProfiles.map((profile) => (
                <Grid item xs={4} key={profile.profileId}>
                  <ProfileCard
                    profile={profile}
                    onProfileArchived={handleProfileArchived}
                    onEditProfile={handleEditProfile}
                  />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Flex
                  justifyContent="center"
                  marginTop="48px"
                  sx={{
                    '& .no-data-placeholder-table-row': {
                      padding: '0 !important'
                    },
                    '& .no-data-placeholder-table-row-title': {
                      marginTop: '20px !important',
                      '& .MuiTypography-root': {
                        fontSize: '20px !important'
                      }
                    },
                    '& .no-data-placeholder-table-row-description': {
                      fontSize: '16px',
                      fontWeight: 400,
                      marginTop: '8px'
                    }
                  }}
                >
                  <div style={{ width: '264px' }}>
                    <NoDataPlaceHolderTableRow
                      title="Add Profile"
                      description="There are currently no profiles to display. All of your profiles will be available on this page."
                      link={
                        <Text
                          variant="body2"
                          link
                          onClick={handleAddProfile}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontWeight: 500,
                            marginTop: '4px',
                            fontSize: '16px'
                          }}
                        >
                          Create a profile
                          <RightArrow style={{ height: '12px', width: '12px' }} />
                        </Text>
                      }
                    />
                  </div>
                </Flex>
              </Grid>
            )}
          </Grid>
          {sortedProfiles && sortedProfiles.length > ITEMS_PER_PAGE ? (
            <Flex justifyContent="flex-end" marginTop="24px">
              <SlPagination
                currentPage={page}
                totalPages={totalPages}
                onClickNext={() => setPage((p) => Math.min(p + 1, totalPages))}
                onClickPrevious={() => setPage((p) => Math.max(p - 1, 1))}
              />
            </Flex>
          ) : null}
        </Flex>
      </ProfileLayout>
    </BasePageContainer>
  );
};
